/**
 * WorkflowDataSourceTab
 *
 * Lists published Data Source workflows for selection in the DataDiscoveryModal.
 * Used as the "Workflow" tab content — fetches workflows on mount and renders
 * a selectable list following DataDiscoveryCard styling patterns.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, GitBranch, Play } from 'lucide-react';
import { workflowApi } from '@/services/api/workflowApi';

interface WorkflowItem {
  id: string;
  name: string;
  description?: string;
  status: string;
  executionCount?: number;
}

interface WorkflowApiItem {
  id?: string;
  _id?: string;
  name?: string;
  description?: string;
  status?: string;
  executionCount?: number;
}

interface WorkflowListResponse {
  data?: WorkflowApiItem[] | { workflows?: WorkflowApiItem[] };
}

interface WorkflowDataSourceTabProps {
  onSelectWorkflow: (workflow: { id: string; name: string }) => void;
}

function getWorkflowList(payload: WorkflowListResponse): WorkflowApiItem[] {
  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return payload.data?.workflows ?? [];
}

function toWorkflowItem(workflow: WorkflowApiItem): WorkflowItem | null {
  const id = workflow.id ?? workflow._id;

  if (!id) {
    return null;
  }

  return {
    id,
    name: workflow.name || 'Untitled Workflow',
    description: workflow.description,
    status: workflow.status || 'published',
    executionCount: workflow.executionCount,
  };
}

export function WorkflowDataSourceTab({ onSelectWorkflow }: WorkflowDataSourceTabProps) {
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadWorkflows = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const payload: WorkflowListResponse = await workflowApi.listWorkflows({ type: 'data_source', status: 'published' });
      const list = getWorkflowList(payload);

      setWorkflows(list.map(toWorkflowItem).filter((workflow): workflow is WorkflowItem => workflow !== null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm">Loading workflows...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-12 text-red-500 dark:text-red-400">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  if (workflows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
        <GitBranch className="h-8 w-8 mb-3 text-gray-300 dark:text-gray-600" />
        <p className="text-sm font-medium">No Data Source workflows found</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Create a Data Source workflow in the workflow editor and publish it to use it here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-center gap-1.5 mb-3">
        <GitBranch className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
          Data Source Workflows ({workflows.length})
        </span>
      </div>
      {workflows.map((workflow) => (
        <button
          key={workflow.id}
          onClick={() => onSelectWorkflow({ id: workflow.id, name: workflow.name })}
          className="w-full text-left p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-emerald-700 dark:group-hover:text-emerald-400">
              {workflow.name}
            </span>
            <Play className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
          </div>
          {workflow.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
              {workflow.description}
            </p>
          )}
          {workflow.executionCount !== undefined && (
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 dark:text-gray-500">
              <span>{workflow.executionCount} executions</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
